import { MATERIAL_VIEW_ID } from '@/utility/constants';
import { getModelImagesForAppearanceAndView } from './modelImageHelpers';

export type ProductTypeImage = {
  viewId: string;
  appearanceId: string;
  modelId?: string;
  model?: ModelImage;
  crop?: Crop;
  tags?: string[];
  badges?: {
    label: string;
    position: 'bottom-left' | 'top-left';
  }[];
};

/** Combined apparel + accessories image-type order (DEV-228118). Skip empty tags. */
export const CYO_IMAGE_TYPE_TAG_ORDER = [
  'model',
  'flatlay',
  'studio',
  'mood',
  'detail',
  'stack',
] as const;

export type CyoImageTypeTag = (typeof CYO_IMAGE_TYPE_TAG_ORDER)[number];

export const getProductTypeModelImagesForView = (
  modelImages: ModelImage[] | undefined,
  appearanceId: string,
  viewId: string,
  crop?: Crop,
  tag?: string
): ProductTypeImage[] => {
  const modelImagesForView = getModelImagesForAppearanceAndView(modelImages, appearanceId, viewId);

  return modelImagesForView
    .filter(
      (image) =>
        !!image.active &&
        (!crop || image.crops.includes(crop) || image.crops.length === 0) &&
        (!tag || image.tags.includes(tag))
    )
    .sort((m1, m2) => m2.priority - m1.priority)
    .map((image) => ({
      viewId,
      appearanceId,
      modelId: image.modelId,
      model: image,
      crop: crop,
      tags: image.tags,
    }));
};

export const getProductTypeMoodImages = (
  modelImages: ModelImage[] | undefined,
  appearanceId: string,
  viewId: string,
  tag?: string
): ProductTypeImage[] => {
  const modelImagesForView = getModelImagesForAppearanceAndView(modelImages, appearanceId, viewId);

  return modelImagesForView
    .filter(
      (image) =>
        !!image.active &&
        // !image.productTypeSizeId && //TODO: What is this?
        !image.crops.length &&
        (!tag || image.tags.includes(tag))
    )
    .sort((m1, m2) => m2.priority - m1.priority)
    .map((image) => ({
      appearanceId,
      viewId,
      model: image,
      modelId: image.modelId,
      crop: undefined,
      tags: image.tags,
    }));
};

const isProductType = (
  productType: ProductType | ClientProductType | AssortmentListProductType
): productType is ProductType | ClientProductType => {
  return productType.hasOwnProperty('defaultValues');
};

/** Remaining model angles after the product type default view (skip if already default). */
const REMAINING_VIEW_PERSPECTIVES = ['front', 'back', 'right', 'left'] as const;

export const getProductTypeImages = (
  productType: ProductType | ClientProductType | AssortmentListProductType,
  modelImages: ModelImage[] | undefined,
  appearanceId: string,
  crop: Crop = 'detail',
  tag: string = 'cyo'
): ProductTypeImage[] => {
  const views = productType.views;
  const badges = productType.labels;
  const defaultViewId = isProductType(productType)
    ? productType.defaultValues.defaultView.id
    : productType.viewId;

  const result: ProductTypeImage[] = [];
  const modelImagesForDefaultView = getProductTypeModelImagesForView(
    modelImages,
    appearanceId,
    defaultViewId,
    crop,
    tag
  );

  const defaultModelImage = modelImagesForDefaultView[0];

  // Default view first (blank + model images)
  result.push({ appearanceId, viewId: defaultViewId, ...badges });
  result.push(...modelImagesForDefaultView);

  const appendedViewIds = new Set<string>([defaultViewId]);

  // Then remaining unique views (e.g. front when default is back) — no duplicates
  for (const perspective of REMAINING_VIEW_PERSPECTIVES) {
    const viewId = views.find((view) => view.perspective === perspective)?.id;
    if (!viewId || appendedViewIds.has(viewId)) continue;

    appendedViewIds.add(viewId);
    result.push(...getProductTypeModelImagesForView(modelImages, appearanceId, viewId, crop, tag));
    result.push({ appearanceId, viewId });
  }

  const moodImages = getProductTypeMoodImages(modelImages, appearanceId, defaultViewId, tag).filter(
    (image) => image.model?.modelId !== defaultModelImage?.model?.modelId
  );

  result.push(...moodImages);

  return result;
};

const matchesCrop = (image: ModelImage, crop: Crop) =>
  image.crops.includes(crop) || image.crops.length === 0;

const toProductTypeImage = (
  image: ModelImage,
  appearanceId: string,
  crop: Crop | undefined
): ProductTypeImage => ({
  appearanceId,
  viewId: image.viewId,
  modelId: image.modelId,
  model: image,
  crop,
  tags: image.tags,
});

const sortByPriorityDesc = (a: ModelImage, b: ModelImage) => b.priority - a.priority;

/**
 * ADP gallery order by tag hierarchy. Within each tag, sort by MIT priority.
 * For `model`, prefer the product type default view first. Blank (no model)
 * product image is appended last as fallback.
 */
export const getProductTypeImagesByTagHierarchy = (
  productType: ProductType | ClientProductType | AssortmentListProductType,
  modelImages: ModelImage[] | undefined,
  appearanceId: string,
  crop: Crop = 'detail',
  channelTag: string = 'cyo'
): ProductTypeImage[] => {
  const badges = productType.labels;
  const defaultViewId = isProductType(productType)
    ? productType.defaultValues.defaultView.id
    : productType.viewId;

  const forAppearance =
    modelImages?.filter(
      (image) =>
        !!image.active &&
        image.appearanceIds.includes(appearanceId) &&
        image.tags.includes(channelTag)
    ) ?? [];

  const usedModelIds = new Set<string>();
  const result: ProductTypeImage[] = [];

  const pushUnique = (images: ModelImage[], imageCrop: Crop | undefined) => {
    for (const image of images) {
      if (usedModelIds.has(image.modelId)) continue;
      usedModelIds.add(image.modelId);
      result.push(toProductTypeImage(image, appearanceId, imageCrop));
    }
  };

  for (const imageTypeTag of CYO_IMAGE_TYPE_TAG_ORDER) {
    const tagged = forAppearance.filter((image) => image.tags.includes(imageTypeTag));
    if (!tagged.length) continue;

    if (imageTypeTag === 'model') {
      const modelTagged = tagged.filter((image) => matchesCrop(image, crop));
      const defaultViewModels = modelTagged
        .filter((image) => image.viewId === defaultViewId)
        .sort(sortByPriorityDesc);
      pushUnique(defaultViewModels, crop);

      const otherViewIds = productType.views
        .map((view) => view.id)
        .filter((viewId) => viewId !== defaultViewId && viewId !== MATERIAL_VIEW_ID);

      for (const viewId of otherViewIds) {
        const forView = modelTagged
          .filter((image) => image.viewId === viewId)
          .sort(sortByPriorityDesc);
        pushUnique(forView, crop);
      }

      // Any remaining model-tagged images (unexpected view ids), still by priority
      pushUnique([...modelTagged].sort(sortByPriorityDesc), crop);
      continue;
    }

    // studio / mood / flatlay / detail / stack
    pushUnique([...tagged].sort(sortByPriorityDesc), crop);
  }

  // Blank product image last (ghostmannequin / no-model fallback) if there are no other images
  if (!result.length) result.push({ appearanceId, viewId: defaultViewId, ...badges });

  return result;
};
